import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import api from "../../utilis/api";
import { update } from "../../redux/store/userSlice";

const Address = () => {
  const { user, token } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  // Initialize state from user
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    company: "",
    street_address_1: "",
    street_address_2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
  });

  useEffect(() => {
    if (user) {
      const names = user.name.trim().split(" ");
      setFormData({
        firstName: names[0],
        lastName: names.slice(1).join(" "),
        phone: user.phone || "",
        email: user.email || "",
        company: user.company || "",
        street_address_1: user.street_address_1 || "",
        street_address_2: user.street_address_2 || "",
        city: user.city || "",
        state: user.state || "",
        zip_code: user.zip_code || "",
        country: user.country || "",
      });
    }
  }, [user]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Combine firstName + lastName only if user doesn't manually edit full name in DB
    const name =
      formData.firstName && formData.lastName
        ? `${formData.firstName} ${formData.lastName}`
        : user.name;

    const updatedData = {
      name,
      phone: formData.phone,
      email: formData.email,
      company: formData.company,
      street_address_1: formData.street_address_1,
      street_address_2: formData.street_address_2,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
      country: formData.country,
    };

    try {
      const res = await api.put(`/auth/update/${user._id}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.meta?.success) {
        toast.success(res.data.meta.message || "Address updated successfully", {
          autoClose: 2000,
        });
        dispatch(update(res.data.data)); // update redux store
      } else {
        toast.error(res.data?.meta?.message || "Update failed", {
          autoClose: 4000,
        });
      }
    } catch (error) {
      const message =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Something went wrong";
      toast.error(message, { autoClose: 5000 });
    }
  };

  return (
    <>
      <Helmet>
        <title>Address Book</title>
        <meta name="description" content={`Welcome to My Account page`} />
      </Helmet>

      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-20 py-10 bg-gradient-to-b from-gray-50 to-white min-h-screen">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800">
            Customer Address
          </h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base">
            Fill your details to continue checkout smoothly
          </p>
        </div>

        {/* Form Card */}
        <form
          className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 sm:p-10 space-y-12"
          onSubmit={handleSubmit}
        >
          {/* ================= Contact ================= */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <span className="w-2 h-6 bg-black rounded"></span>
              Contact Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="text"
                name="company"
                placeholder="Company (optional)"
                value={formData.company}
                onChange={handleChange}
                className="w-full md:col-span-2 border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />
            </div>
          </div>

          {/* ================= Address ================= */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <span className="w-2 h-6 bg-black rounded"></span>
              Address Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                type="text"
                name="street_address_1"
                placeholder="Street Address"
                value={formData.street_address_1}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="text"
                name="street_address_2"
                placeholder="Street Address 2"
                value={formData.street_address_2}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="text"
                name="city"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="text"
                name="state"
                placeholder="Province / State"
                value={formData.state}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="text"
                name="zip_code"
                placeholder="Zip Code"
                value={formData.zip_code}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />

              <input
                type="text"
                name="country"
                placeholder="Country"
                value={formData.country}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4 flex justify-start">
            <button
              type="submit"
              className="bg-black text-white px-8 py-3 rounded-xl hover:bg-gray-800 active:scale-95 transition-all duration-300 shadow-md"
            >
              Save Address
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Address;
